import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';

import { UserManagement } from '@apps/web/components/settings/user_management';
import { Badge } from '@apps/web/components/ui/badge';
import { Card, CardContent } from '@apps/web/components/ui/card';

type PreOnboardingSettingsViewProps = {
    readonly organizationId: string | null;
    readonly organizationName: string | null | undefined;
};

/**
 * Constrained settings view for pre-onboarding-restricted orgs (pre-onboarding
 * stage AND not a platform admin). Replicates the deleted monolith's
 * team-management-only page and short-circuits the full settings shell.
 *
 * The dedicated settings shell hides the main app nav, so this view adds a
 * back-to-home affordance; without it a restricted user would be stuck here.
 */
export function PreOnboardingSettingsView({
    organizationId,
    organizationName,
}: PreOnboardingSettingsViewProps) {
    return (
        <div className="container mx-auto max-w-4xl space-y-6 p-6">
            <Link
                to="/"
                className="text-muted-foreground hover:bg-accent hover:text-foreground inline-flex items-center gap-1.5 rounded-md p-1 text-sm transition-colors"
                aria-label="Back to main app"
            >
                <ArrowLeft className="h-4 w-4" />
                Back to main app
            </Link>
            <div className="space-y-2">
                <Badge
                    variant="secondary"
                    className="bg-emerald-100 text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-300"
                >
                    Pre-onboarding
                </Badge>
                <h1 className="text-3xl font-bold">Settings</h1>
                <p className="text-muted-foreground max-w-2xl text-sm">
                    Manage team access for{' '}
                    {organizationName ?? 'your organization'} while Phoebe gets
                    your workspace ready.
                </p>
            </div>
            {organizationId ? (
                <UserManagement organizationId={organizationId} />
            ) : (
                <Card className="shadow-none">
                    <CardContent className="p-6">
                        <p className="text-muted-foreground text-sm">
                            Select an organization to manage team members.
                        </p>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
